"use client";

import { c as _c } from "react/compiler-runtime";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import TransferSingle from "@/components/nft-transfer/TransferSingle";
import { NEXTGEN_CONTRACT } from "@/constants/constants";
import type { NextGenCollection, NextGenToken, NextGenTrait } from "@/entities/INextgen";
import { CollectedCollectionType } from "@/entities/IProfile";
import { areEqualAddresses, isNullAddress } from "@/helpers/Helpers";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { ContractType, NextgenCollectionView } from "@/types/enums";
import { faChevronCircleLeft, faChevronCircleRight, faFire } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useMemo } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Tooltip } from "react-tooltip";
import { printViewButton } from "../collectionParts/NextGenCollection";
import { NextGenBackToCollectionPageLink } from "../collectionParts/NextGenCollectionHeader";
import styles from "../NextGen.module.scss";
import NextGenTokenAbout from "./NextGenTokenAbout";
import NextGenTokenArt from "./NextGenTokenArt";
import NextgenTokenRarity, { NextgenTokenTraits } from "./NextGenTokenProperties";
import NextGenTokenProvenance from "./NextGenTokenProvenance";
import NextGenTokenRenderCenter from "./NextGenTokenRenderCenter";
interface Props {
  collection: NextGenCollection;
  token: NextGenToken;
  traits: NextGenTrait[];
  tokenCount: number;
  view: NextgenCollectionView;
  setView: (view: NextgenCollectionView) => void;
}
export default function NextGenTokenPage(props) {
  const $ = _c(32);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    address: connectedAddress
  } = useSeizeConnectContext();
  const isConnectedAddressOwner = areEqualAddresses(connectedAddress, props.token.owner);
  const isMdUp = useMediaQuery("(min-width: 768px)");
  let t0;
  bb0: {
    if (!isConnectedAddressOwner) {
      t0 = null;
      break bb0;
    }
    const t1 = props.token.name ?? `6529 NextGen #${props.token.id}`;
    let t2;
    if ($[0] !== props.token.id || $[1] !== props.token.thumbnail_url || $[2] !== t1) {
      t2 = <div className="mb-4"><TransferSingle collectionType={CollectedCollectionType.NEXTGEN} contractType={ContractType.ERC721} contract={NEXTGEN_CONTRACT} tokenId={props.token.id} max={1} title={t1} thumbUrl={props.token.thumbnail_url} /></div>;
      $[0] = props.token.id;
      $[1] = props.token.thumbnail_url;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t0 = t2;
  }
  const transferSingle = t0;
  let t1;
  if ($[4] !== isMdUp || $[5] !== props.collection || $[6] !== props.setView || $[7] !== props.token || $[8] !== props.tokenCount || $[9] !== props.traits || $[10] !== props.view || $[11] !== transferSingle) {
    t1 = function printDetails() {
      return <Container className="pt-4"><Row><Col className="d-flex gap-4">{printViewButton(props.view, NextgenCollectionView.ABOUT, props.setView)}{printViewButton(props.view, NextgenCollectionView.PROVENANCE, props.setView)}{printViewButton(props.view, NextgenCollectionView.DISPLAY_CENTER, props.setView)}{printViewButton(props.view, NextgenCollectionView.RARITY, props.setView)}</Col></Row><Row className="pt-4 pb-4">{props.view === NextgenCollectionView.ABOUT && <><Col xs={12}>{isMdUp ? null : transferSingle}</Col><Col sm={12} md={6}><NextGenTokenAbout collection={props.collection} token={props.token} /></Col><Col sm={12} md={6}>{isMdUp ? transferSingle : null}<NextgenTokenTraits collection={props.collection} token={props.token} traits={props.traits.filter(_temp)} tokenCount={props.tokenCount} /></Col></>}{props.view === NextgenCollectionView.PROVENANCE && <Col className="pt-4 pb-4"><NextGenTokenProvenance token_id={props.token.id} collection={props.collection} /></Col>}{props.view === NextgenCollectionView.DISPLAY_CENTER && <Col className="pt-4 pb-4"><NextGenTokenRenderCenter token={props.token} /></Col>}{props.view === NextgenCollectionView.RARITY && <Col className="pt-4 pb-4"><NextgenTokenRarity collection={props.collection} token={props.token} traits={props.traits} tokenCount={props.tokenCount} /></Col>}</Row></Container>;
    };
    $[4] = isMdUp;
    $[5] = props.collection;
    $[6] = props.setView;
    $[7] = props.token;
    $[8] = props.tokenCount;
    $[9] = props.traits;
    $[10] = props.view;
    $[11] = transferSingle;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  const printDetails = t1;
  let t2;
  if ($[13] !== pathname || $[14] !== props.token.id || $[15] !== props.token.normalised_id || $[16] !== router || $[17] !== searchParams) {
    t2 = function printPreviousToken() {
      const hasPreviousToken = props.token.normalised_id > 0;
      const prev = <FontAwesomeIcon icon={faChevronCircleLeft} data-tooltip-id={hasPreviousToken ? `prev-token-${props.token.id}` : undefined} onClick={() => {
        if (!hasPreviousToken) {
          return;
        }
        const newPathname = pathname.replace(/\/(\d+)(\/?)$/, `/${props.token.id - 1}$2`);
        const query = searchParams.toString();
        router.push(query ? `${newPathname}?${query}` : newPathname, {
          scroll: false
        });
      }} style={{
        height: "35px",
        color: hasPreviousToken ? "#fff" : "#9a9a9a",
        cursor: hasPreviousToken ? "pointer" : "default"
      }} />;
      return <>{prev}{hasPreviousToken && <Tooltip id={`prev-token-${props.token.id}`} delayShow={250} variant="light" style={{
          padding: "4px 8px"
        }}>Previous Token</Tooltip>}</>;
    };
    $[13] = pathname;
    $[14] = props.token.id;
    $[15] = props.token.normalised_id;
    $[16] = router;
    $[17] = searchParams;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  const printPreviousToken = t2;
  let t3;
  if ($[19] !== pathname || $[20] !== props.token.id || $[21] !== props.token.normalised_id || $[22] !== props.tokenCount || $[23] !== router || $[24] !== searchParams) {
    t3 = function printNextToken() {
      const hasNextToken = props.tokenCount - 1 > props.token.normalised_id;
      const next = <FontAwesomeIcon icon={faChevronCircleRight} data-tooltip-id={hasNextToken ? `next-token-${props.token.id}` : undefined} onClick={() => {
        if (!hasNextToken) {
          return;
        }
        const newPathname_0 = pathname.replace(/\/(\d+)(\/?)$/, `/${props.token.id + 1}$2`);
        const query_0 = searchParams.toString();
        router.push(query_0 ? `${newPathname_0}?${query_0}` : newPathname_0, {
          scroll: false
        });
      }} style={{
        height: "35px",
        color: hasNextToken ? "#fff" : "#9a9a9a",
        cursor: hasNextToken ? "pointer" : "default"
      }} />;
      return <>{next}{hasNextToken && <Tooltip id={`next-token-${props.token.id}`} delayShow={250} variant="light" style={{
          padding: "4px 8px"
        }}>Next Token</Tooltip>}</>;
    };
    $[19] = pathname;
    $[20] = props.token.id;
    $[21] = props.token.normalised_id;
    $[22] = props.tokenCount;
    $[23] = router;
    $[24] = searchParams;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  const printNextToken = t3;
  let t4;
  if ($[26] !== printDetails || $[27] !== printNextToken || $[28] !== printPreviousToken || $[29] !== props.collection || $[30] !== props.token) {
    const printToken = function printToken() {
      return <><Container fluid={true} className={`${styles.tokenContainer} pt-4 pb-4`}><Row><Col><Container><Row className="pb-2"><Col className="d-flex align-items-center justify-content-between"><span className="d-flex flex-column"><span className="d-flex gap-3"><h1 className="mb-0 font-color">{props.token.name}</h1>{(props.token.burnt || isNullAddress(props.token.owner)) && <><FontAwesomeIcon icon={faFire} data-tooltip-id={`burnt-token-${props.token.id}`} style={{
                            height: "35px",
                            color: "#c51d34"
                          }} /><Tooltip id={`burnt-token-${props.token.id}`} style={{
                            backgroundColor: "#1F2937",
                            color: "white",
                            padding: "4px 8px"
                          }}>Burnt</Tooltip></>}</span><NextGenBackToCollectionPageLink collection={props.collection} /></span><span className="d-flex gap-2">{printPreviousToken()}{printNextToken()}</span></Col></Row></Container><NextGenTokenArt token={props.token} collection={props.collection} /></Col></Row></Container>{printDetails()}</>;
    };
    t4 = <>{printToken()}</>;
    $[26] = printDetails;
    $[27] = printNextToken;
    $[28] = printPreviousToken;
    $[29] = props.collection;
    $[30] = props.token;
    $[31] = t4;
  } else {
    t4 = $[31];
  }
  return t4;
}
function _temp(trait) {
  return trait.trait !== "Collection Name";
}